/**
 * BroadcastComposer — modal for composing, audience-targeting, testing,
 * and sending or scheduling a broadcast email.
 *
 * Two columns:
 *   Left:  Composer (Name, Subject, Preheader, HTML Body)
 *          + Merge token cheatsheet + AI draft button
 *   Right: Audience filters + live recipient count
 *          + Test send + Send Now / Schedule
 *
 * Statuses:
 *   draft / scheduled / failed → fully editable
 *   sending / sent              → read-only
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Save, Send, CalendarClock, Sparkles, Eye, Loader2,
  CheckCircle2, AlertTriangle, Users, Beaker, Wand2, Target, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'trial',                 label: 'Trial' },
  { value: 'active',                label: 'Active (paid)' },
  { value: 'trial_pending_payment', label: 'Trial pending payment' },
  { value: 'trial_registered',      label: 'Trial registered' },
  { value: 'trial_expired',         label: 'Trial expired' },
  { value: 'cancelled',             label: 'Cancelled' },
  { value: 'payment_failed',        label: 'Payment failed' },
  { value: 'beta',                  label: 'Beta (founding)' },
  { value: 'none',                  label: '(no status set)' },
];
const SOURCE_OPTIONS = [
  { value: 'samcart',    label: 'SamCart purchase' },
  { value: 'beta_trial', label: 'Beta trial signup' },
  { value: 'none',       label: '(no source set)' },
];

const MERGE_TOKENS = [
  '{{first_name}}', '{{last_name}}', '{{practice_name}}',
  '{{email}}', '{{login_url}}', '{{phone}}', '{{trial_end_date}}',
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

// Compose a "datetime-local" string in the user's local TZ from a Date.
const toLocalInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function BroadcastComposer({ adminToken, broadcastId, onClose }) {
  const isNew = !broadcastId;

  // Form state
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [htmlBody, setHtmlBody] = useState('<p>Hi {{first_name}},</p>\n\n<p>Write your message here.</p>\n\n<p>- Cary</p>');
  const [cohortKey, setCohortKey] = useState('');
  const [statusFilters, setStatusFilters] = useState(['trial', 'active', 'trial_pending_payment', 'trial_registered', 'beta']);
  const [sourceFilters, setSourceFilters] = useState([]);
  const [includeLeads, setIncludeLeads] = useState(false);
  const [leadsOnly, setLeadsOnly] = useState(false);
  const [excludeEmailsText, setExcludeEmailsText] = useState('');
  const [customEmailsText, setCustomEmailsText] = useState('');
  const [cohorts, setCohorts] = useState([]);

  // Meta
  const [status, setStatus] = useState('draft');
  const [stats, setStats] = useState(null);
  const [sentAt, setSentAt] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);

  // UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [audience, setAudience] = useState({ total: 0, sample: [] });
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('cganz2279@gmail.com');
  const [scheduleAt, setScheduleAt] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30);
    return toLocalInputValue(d);
  });
  // --- Smart Send Time (per-recipient timezone delivery) ---
  const [smartSend, setSmartSend] = useState(false);
  const [smartDate, setSmartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [smartHour, setSmartHour] = useState(9);
  const [useLearnedHours, setUseLearnedHours] = useState(false);
  const [bestHourStats, setBestHourStats] = useState(null);
  const [showAi, setShowAi] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('warm');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiFieldBusy, setAiFieldBusy] = useState(null); // 'subject' | 'preheader' | null

  const readOnly = status === 'sending' || status === 'sent';

  // Build audience payload
  const audiencePayload = useMemo(() => ({
    cohortKey: cohortKey || undefined,
    statusFilters,
    sourceFilters,
    includeLeadMagnetLeads: includeLeads || leadsOnly,
    leadMagnetLeadsOnly: leadsOnly,
    customEmails:  customEmailsText.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean),
    excludeEmails: excludeEmailsText.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean),
  }), [cohortKey, statusFilters, sourceFilters, includeLeads, leadsOnly, customEmailsText, excludeEmailsText]);

  const fetchAudience = async (payload) => {
    try {
      const res = await fetch(`${API}/api/admin/broadcasts/audience-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ audience: payload }),
      });
      if (!res.ok) throw new Error(`Audience preview failed (${res.status})`);
      return await res.json();
    } catch (e) {
      console.error(e);
      return { total: 0, sample: [] };
    }
  };

  // Live audience preview (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      const data = await fetchAudience(audiencePayload);
      setAudience(data);
    }, 400);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [JSON.stringify(audiencePayload)]);

  // Load cohort catalog + best-hour stats
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/broadcasts/cohorts`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCohorts(data.cohorts || []);
        }
      } catch (e) { /* non-fatal */ }
      try {
        const res2 = await fetch(`${API}/api/admin/broadcasts/best-hours/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        if (res2.ok) setBestHourStats(await res2.json());
      } catch (e) { /* non-fatal */ }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Load existing broadcast
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/broadcasts/${broadcastId}`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        if (!res.ok) throw new Error(`Load failed: ${res.status}`);
        const b = await res.json();
        setName(b.name || '');
        setSubject(b.subject || '');
        setPreheader(b.preheader || '');
        setHtmlBody(b.htmlBody || '');
        const a = b.audience || {};
        setCohortKey(a.cohortKey || '');
        setStatusFilters(a.statusFilters || []);
        setSourceFilters(a.sourceFilters || []);
        setIncludeLeads(!!a.includeLeadMagnetLeads);
        setLeadsOnly(!!a.leadMagnetLeadsOnly);
        setCustomEmailsText((a.customEmails || []).join('\n'));
        setExcludeEmailsText((a.excludeEmails || []).join('\n'));
        setStatus(b.status || 'draft');
        setStats(b.stats || null);
        setSentAt(b.sentAt);
        setScheduledAt(b.scheduledAt);
        setCreatedAt(b.createdAt);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [broadcastId]);

  const flash = (msg, kind = 'ok') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  // Save / create
  const persist = async () => {
    setSaving(true);
    setErr(null);
    const body = {
      name, subject, htmlBody, preheader,
      audience: audiencePayload,
    };
    try {
      let res;
      if (isNew) {
        res = await fetch(`${API}/api/admin/broadcasts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/api/admin/broadcasts/${broadcastId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const data = await res.json();
      flash('Saved');
      return data;
    } catch (e) {
      setErr(e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Test
  const sendTest = async () => {
    if (!testEmail.trim()) { setErr('Enter a test email first.'); return; }
    setSending(true);
    setErr(null);
    try {
      const saved = await persist();
      const id = saved.id;
      const res = await fetch(`${API}/api/admin/broadcasts/${id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ testEmail }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Test failed (${res.status}): ${t.slice(0, 160)}`);
      }
      const data = await res.json();
      flash(`Test sent → ${data.sent}/${(data.sent || 0) + (data.failed || 0)} delivered to ${data.to?.join(', ')}`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  // Send now
  const sendNow = async () => {
    const total = audience.total;
    if (!window.confirm(`Send this email to ${total} recipient(s) right now? This cannot be undone.`)) return;
    setSending(true);
    setErr(null);
    try {
      const saved = await persist();
      const id = saved.id;
      const res = await fetch(`${API}/api/admin/broadcasts/${id}/send-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Send failed (${res.status}): ${t.slice(0, 160)}`);
      }
      const data = await res.json();
      // Send Now stats include `unsubscribedSkipped` from CAN-SPAM filter
      const s = data.stats || {};
      const skipNote = s.unsubscribedSkipped ? ` (${s.unsubscribedSkipped} unsubscribed skipped)` : '';
      flash(`Sent ${s.sent}/${s.recipients}${s.failed ? ` (${s.failed} failed)` : ''}${skipNote}`);
      setStatus('sent');
      setStats(s);
      setSentAt(new Date().toISOString());
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  // Schedule
  const schedule = async () => {
    setSending(true);
    setErr(null);
    try {
      const saved = await persist();
      const id = saved.id;
      let res;
      if (smartSend) {
        if (!smartDate) { setErr('Pick a date first.'); setSending(false); return; }
        res = await fetch(`${API}/api/admin/broadcasts/${id}/schedule-smart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            localDate: smartDate, localHour: smartHour, localMinute: 0,
            useLearnedHours: useLearnedHours,
          }),
        });
      } else {
        if (!scheduleAt) { setErr('Pick a future date/time first.'); setSending(false); return; }
        const isoLocal = new Date(scheduleAt);
        if (isNaN(isoLocal.getTime())) { setErr('Invalid date'); setSending(false); return; }
        if (isoLocal <= new Date()) { setErr('Schedule must be in the future.'); setSending(false); return; }
        res = await fetch(`${API}/api/admin/broadcasts/${id}/schedule`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ scheduledAt: isoLocal.toISOString() }),
        });
      }
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Schedule failed (${res.status}): ${t.slice(0, 200)}`);
      }
      const data = await res.json();
      if (smartSend) {
        const s = data.smartSendStats || {};
        const buckets = Object.entries(s.tzBuckets || {}).map(([tz, n]) => `${n} ${tz.split('/').pop()}`).join(' · ');
        const learned = s.learnedHourCount > 0 ? ` · ${s.learnedHourCount} learned-hour overrides` : '';
        flash(`Scheduled ${s.enqueued} recipients · earliest ${fmtDate(s.earliestUtc)} · ${buckets}${learned}`);
      } else {
        flash(`Scheduled for ${fmtDate(data.scheduledAt)}`);
      }
      setStatus('scheduled');
      setScheduledAt(data.scheduledAt);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  // Per-field AI (subject only, preheader only)
  const aiGenField = async (field) => {
    setAiFieldBusy(field);
    setErr(null);
    try {
      const audienceSummary = (() => {
        const parts = [];
        if (cohortKey) {
          const c = cohorts.find(x => x.key === cohortKey);
          if (c) parts.push(c.label.toLowerCase());
        } else {
          if (statusFilters.length) parts.push(`subscription status: ${statusFilters.join(', ')}`);
          if (sourceFilters.length)  parts.push(`source: ${sourceFilters.join(', ')}`);
        }
        if (leadsOnly) parts.push('lead-magnet leads only');
        else if (includeLeads) parts.push('+ lead-magnet leads');
        return parts.join(' · ') || 'PractCom subscribers';
      })();
      const res = await fetch(`${API}/api/admin/broadcasts/ai-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          field, htmlBody, subject,
          audienceSummary, tone: aiTone,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`AI ${field} failed (${res.status}): ${t.slice(0, 160)}`);
      }
      const data = await res.json();
      if (field === 'subject')   setSubject(data.value || '');
      if (field === 'preheader') setPreheader(data.value || '');
      flash(`AI rewrote the ${field}. Click again for another variant.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setAiFieldBusy(null);
    }
  };

  // AI draft
  const generateDraft = async () => {
    if (!aiPrompt.trim()) { setErr('Tell the AI what to write about first.'); return; }
    setAiBusy(true);
    setErr(null);
    try {
      const audienceSummary = (() => {
        const parts = [];
        if (statusFilters.length) parts.push(`subscription status: ${statusFilters.join(', ')}`);
        if (sourceFilters.length)  parts.push(`source: ${sourceFilters.join(', ')}`);
        if (leadsOnly) parts.push('lead-magnet leads only');
        else if (includeLeads) parts.push('+ lead-magnet leads');
        return parts.join(' · ') || 'all PractCom subscribers';
      })();
      const res = await fetch(`${API}/api/admin/broadcasts/ai-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ prompt: aiPrompt, tone: aiTone, audienceSummary }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`AI draft failed (${res.status}): ${t.slice(0, 200)}`);
      }
      const data = await res.json();
      if (data.subject)   setSubject(data.subject);
      if (data.preheader) setPreheader(data.preheader);
      if (data.htmlBody)  setHtmlBody(data.htmlBody);
      flash('AI draft loaded. Review and edit before sending.');
      setShowAi(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setAiBusy(false);
    }
  };

  // Multi-checkbox helper
  const toggleArray = (arr, val, setter) => {
    if (arr.includes(val)) setter(arr.filter(x => x !== val));
    else setter([...arr, val]);
  };

  const insertToken = (token) => {
    setHtmlBody(prev => prev + token);
  };

  // Preview iframe (renders htmlBody as-is with merge tokens visible)
  const previewSrc = useMemo(() => {
    const sample = audience.sample?.[0];
    const ctx = {
      first_name:    sample?.first_name || 'Sarah',
      last_name:     'Example',
      practice_name: sample?.practice_name || 'Example Dental',
      email:         sample?.email || 'sample@example.com',
      login_url:     'https://app.practcom.com/login',
      phone:         '(555) 123-4567',
      trial_end_date: 'June 15, 2026',
    };
    const render = (s) => s.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (m, k) => (ctx[k] !== undefined ? ctx[k] : m));
    const body = render(htmlBody || '');
    return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f1f5f9;font-family:Georgia,serif;">
      <div style="max-width:620px;margin:24px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 16px rgba(0,0,0,.06);">
        <div style="background:linear-gradient(135deg,#1e3a8a,#2563eb);padding:24px 28px;">
          <h1 style="color:#fff;margin:0;font-size:22px;letter-spacing:.3px;">PractCom</h1>
          <p style="color:#bfdbfe;margin:4px 0 0;font-size:13px;">The Total Practice Communication Solution</p>
        </div>
        <div style="padding:28px 32px;color:#1f2937;font-size:16px;line-height:1.7;">
          ${body}
          <hr style="margin:28px 0 18px;border:0;border-top:1px solid #e2e8f0;">
          <p style="margin:0;font-size:14px;color:#475569;line-height:1.6;">
            <strong>Cary H. Ganz DDS</strong><br>Founder, PractCom<br>
            <a href="mailto:admin@practcom.com" style="color:#2563eb;text-decoration:none;">admin@practcom.com</a>
          </p>
        </div>
        <div style="background:#0f172a;padding:14px;text-align:center;">
          <p style="margin:0;color:#94a3b8;font-size:11px;">Built by dentists, for dentists. © 2026 PractCom.</p>
        </div>
      </div>
    </body></html>`;
  }, [htmlBody, audience.sample]);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-slate-900/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="broadcast-composer"
    >
      <div className="bg-white w-full sm:max-w-6xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-violet-50">
          <div>
            <h2 className="text-lg font-extrabold text-indigo-900">
              {isNew ? 'New Broadcast' : `Edit Broadcast`}
            </h2>
            <p className="text-xs text-indigo-700 mt-0.5">
              Status: <b>{status}</b>
              {scheduledAt && status === 'scheduled' && <> · scheduled for {fmtDate(scheduledAt)}</>}
              {sentAt && <> · sent {fmtDate(sentAt)} ({stats?.sent ?? 0}/{stats?.recipients ?? 0})</>}
              {createdAt && !isNew && <> · created {fmtDate(createdAt)}</>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-slate-100"
            data-testid="broadcast-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-0">
          {/* Composer */}
          <div className="p-5 space-y-4 border-r border-slate-200">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Internal name (admin only)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    disabled={readOnly}
                    placeholder="e.g. April newsletter — check-in feature push"
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-indigo-500 outline-none text-sm"
                    data-testid="broadcast-name"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-700">Subject line</label>
                    <button
                      type="button"
                      onClick={() => aiGenField('subject')}
                      disabled={readOnly || aiFieldBusy === 'subject'}
                      title={subject ? 'Regenerate subject' : 'Generate subject with AI'}
                      className="text-[11px] font-bold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-violet-50 disabled:opacity-50"
                      data-testid="broadcast-ai-subject"
                    >
                      {aiFieldBusy === 'subject'
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : subject ? <RefreshCw className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                      {subject ? 'AI: rewrite' : 'AI: write subject'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    disabled={readOnly}
                    placeholder="A short, plain-spoken subject. Merge tokens OK."
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-indigo-500 outline-none text-sm"
                    data-testid="broadcast-subject"
                  />
                  {subject && (
                    <p className={`mt-1 text-[10px] font-bold ${
                      subject.length > 60 ? 'text-rose-600' : subject.length > 50 ? 'text-amber-600' : 'text-slate-400'
                    }`}>
                      {subject.length} character{subject.length === 1 ? '' : 's'}
                      {subject.length > 60 && ' — may be truncated in inboxes'}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-700">Preheader (inbox preview line)</label>
                    <button
                      type="button"
                      onClick={() => aiGenField('preheader')}
                      disabled={readOnly || aiFieldBusy === 'preheader'}
                      title={preheader ? 'Regenerate preheader' : 'Generate preheader with AI'}
                      className="text-[11px] font-bold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-violet-50 disabled:opacity-50"
                      data-testid="broadcast-ai-preheader"
                    >
                      {aiFieldBusy === 'preheader'
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : preheader ? <RefreshCw className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                      {preheader ? 'AI: rewrite' : 'AI: write preheader'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={preheader}
                    onChange={e => setPreheader(e.target.value)}
                    disabled={readOnly}
                    placeholder="The short line that shows next to the subject in Gmail/Apple Mail"
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-indigo-500 outline-none text-sm"
                    data-testid="broadcast-preheader"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-700">HTML body</label>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setShowPreview(p => !p)}
                        className="text-xs text-indigo-700 hover:text-indigo-900 font-bold inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-50"
                        data-testid="broadcast-preview-toggle"
                      >
                        <Eye className="w-3.5 h-3.5" /> {showPreview ? 'Hide preview' : 'Show preview'}
                      </button>
                      <button
                        onClick={() => setShowAi(s => !s)}
                        disabled={readOnly}
                        className="text-xs text-violet-700 hover:text-violet-900 font-bold inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-violet-50 disabled:opacity-50"
                        data-testid="broadcast-ai-toggle"
                      >
                        <Wand2 className="w-3.5 h-3.5" /> {showAi ? 'Close AI' : 'Ask AI to draft'}
                      </button>
                    </div>
                  </div>

                  {showAi && (
                    <div className="bg-violet-50 border-2 border-violet-200 rounded-lg p-3 mb-2 space-y-2">
                      <p className="text-xs text-violet-900 font-bold flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" /> Tell Cary's copywriter what to write
                      </p>
                      <p className="text-[11px] text-violet-800 leading-snug">
                        Give a one-sentence brief — what's the announcement, who is it for,
                        what action do you want. The AI returns a complete subject + body
                        in your voice with merge tokens already filled in.
                        It's faster than writing in ChatGPT and pasting because the AI knows
                        your audience filters, your brand, and your existing template.
                      </p>
                      <textarea
                        value={aiPrompt}
                        onChange={e => setAiPrompt(e.target.value)}
                        placeholder="e.g. Promote our new 24-hour check-in feature to trial practices who haven't activated it yet. One CTA: go turn it on."
                        rows={3}
                        className="w-full px-3 py-2 border-2 border-violet-200 rounded text-sm focus:border-violet-500 outline-none"
                        data-testid="broadcast-ai-prompt"
                      />
                      <div className="flex items-center gap-2">
                        <select
                          value={aiTone}
                          onChange={e => setAiTone(e.target.value)}
                          className="text-sm border-2 border-violet-200 rounded px-2 py-1 focus:border-violet-500 outline-none"
                          data-testid="broadcast-ai-tone"
                        >
                          <option value="warm">Tone: warm</option>
                          <option value="direct">Tone: direct</option>
                          <option value="playful">Tone: playful</option>
                          <option value="formal">Tone: formal</option>
                        </select>
                        <button
                          onClick={generateDraft}
                          disabled={aiBusy}
                          className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold px-3 py-1.5 rounded inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid="broadcast-ai-generate"
                        >
                          {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          Generate draft
                        </button>
                        <span className="text-[11px] text-violet-700">Replaces subject + body. Always edit before sending.</span>
                      </div>
                    </div>
                  )}

                  <textarea
                    value={htmlBody}
                    onChange={e => setHtmlBody(e.target.value)}
                    disabled={readOnly}
                    rows={14}
                    spellCheck="true"
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-indigo-500 outline-none text-sm font-mono"
                    data-testid="broadcast-body"
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-[11px] text-slate-500 mr-1">Insert token:</span>
                    {MERGE_TOKENS.map(t => (
                      <button
                        key={t}
                        onClick={() => insertToken(t)}
                        disabled={readOnly}
                        className="text-[11px] font-mono px-1.5 py-0.5 bg-slate-100 hover:bg-indigo-100 rounded border border-slate-200 disabled:opacity-50"
                        data-testid={`broadcast-token-${t.replace(/[{}\s]/g, '')}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {showPreview && (
                  <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-3 py-1.5 text-[11px] font-bold text-slate-600 flex items-center gap-2">
                      <Eye className="w-3 h-3" /> Live preview (tokens filled with first recipient or placeholders)
                    </div>
                    <iframe
                      title="Email preview"
                      srcDoc={previewSrc}
                      sandbox=""
                      className="w-full h-[520px] bg-white"
                      data-testid="broadcast-preview-iframe"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="p-5 space-y-4 bg-slate-50 lg:max-h-full lg:overflow-auto">
            {/* Audience */}
            <div className="bg-white border-2 border-indigo-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm font-extrabold text-indigo-900">Audience</h3>
                <span
                  className="ml-auto text-xs font-extrabold bg-indigo-600 text-white px-2 py-0.5 rounded-full"
                  data-testid="broadcast-audience-count"
                >
                  {audience.total} recipients
                </span>
              </div>

              {/* COHORT PRESETS */}
              <div className="mb-3">
                <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Target className="w-3 h-3" /> Quick cohort (one-click target)
                </p>
                <select
                  value={cohortKey}
                  onChange={e => setCohortKey(e.target.value)}
                  disabled={readOnly}
                  className="w-full px-2 py-1.5 border-2 border-indigo-200 rounded text-sm bg-white focus:border-indigo-500 outline-none"
                  data-testid="broadcast-cohort-select"
                >
                  <option value="">— Custom filters (use checkboxes below) —</option>
                  {cohorts.map(c => (
                    <option key={c.key} value={c.key}>
                      {c.label} {c.count !== null && c.count !== undefined ? `(${c.count})` : ''}
                    </option>
                  ))}
                </select>
                {cohortKey && (
                  <p className="mt-1 text-[11px] text-indigo-700 leading-snug">
                    {cohorts.find(c => c.key === cohortKey)?.description}
                    <button
                      onClick={() => setCohortKey('')}
                      className="ml-2 underline text-indigo-800 hover:text-indigo-900 font-bold"
                      data-testid="broadcast-cohort-clear"
                    >Clear cohort</button>
                  </p>
                )}
              </div>

              {/* Manual filters — dimmed when a cohort is active */}
              <div className={cohortKey ? 'opacity-50 pointer-events-none' : ''}>
                <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">Status</p>
              <div className="space-y-1 mb-3">
                {STATUS_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-indigo-50 px-1 py-0.5 rounded">
                    <input
                      type="checkbox"
                      checked={statusFilters.includes(opt.value)}
                      onChange={() => toggleArray(statusFilters, opt.value, setStatusFilters)}
                      disabled={readOnly || leadsOnly}
                      data-testid={`broadcast-status-${opt.value}`}
                    />
                    <span className={leadsOnly ? 'text-slate-400' : ''}>{opt.label}</span>
                  </label>
                ))}
              </div>

              <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">Source</p>
              <div className="space-y-1 mb-3">
                {SOURCE_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-indigo-50 px-1 py-0.5 rounded">
                    <input
                      type="checkbox"
                      checked={sourceFilters.includes(opt.value)}
                      onChange={() => toggleArray(sourceFilters, opt.value, setSourceFilters)}
                      disabled={readOnly || leadsOnly}
                      data-testid={`broadcast-source-${opt.value}`}
                    />
                    <span className={leadsOnly ? 'text-slate-400' : ''}>{opt.label}</span>
                  </label>
                ))}
              </div>

              <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">Lead-magnet leads</p>
              <label className="flex items-center gap-2 text-xs cursor-pointer hover:bg-indigo-50 px-1 py-0.5 rounded">
                <input
                  type="checkbox"
                  checked={includeLeads}
                  onChange={e => { setIncludeLeads(e.target.checked); if (!e.target.checked) setLeadsOnly(false); }}
                  disabled={readOnly}
                  data-testid="broadcast-include-leads"
                />
                <span>Include free PDF leads</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer hover:bg-indigo-50 px-1 py-0.5 rounded mb-3">
                <input
                  type="checkbox"
                  checked={leadsOnly}
                  onChange={e => { setLeadsOnly(e.target.checked); if (e.target.checked) setIncludeLeads(true); }}
                  disabled={readOnly}
                  data-testid="broadcast-leads-only"
                />
                <span><b>Only</b> lead-magnet leads</span>
              </label>
              </div>{/* end dimmed manual filters */}

              <details className="text-xs">
                <summary className="cursor-pointer font-bold text-slate-700 hover:text-indigo-700">Advanced (custom / exclude emails)</summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block">Also send to (one per line):</label>
                    <textarea
                      value={customEmailsText}
                      onChange={e => setCustomEmailsText(e.target.value)}
                      disabled={readOnly}
                      rows={2}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-xs font-mono focus:border-indigo-500 outline-none"
                      data-testid="broadcast-custom-emails"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block">Exclude these emails:</label>
                    <textarea
                      value={excludeEmailsText}
                      onChange={e => setExcludeEmailsText(e.target.value)}
                      disabled={readOnly}
                      rows={2}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-xs font-mono focus:border-indigo-500 outline-none"
                      data-testid="broadcast-exclude-emails"
                    />
                  </div>
                </div>
              </details>

              {audience.sample?.length > 0 && (
                <div className="mt-2 border-t border-slate-100 pt-2">
                  <p className="text-[11px] font-bold text-slate-700 mb-1">First few recipients:</p>
                  <ul className="text-[11px] text-slate-600 space-y-0.5 max-h-32 overflow-auto">
                    {audience.sample.map((s, i) => (
                      <li key={i} className="truncate" title={s.email}>
                        <b>{s.first_name}</b> @ {s.practice_name} <span className="text-slate-400">— {s.email}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Test */}
            <div className="bg-white border-2 border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Beaker className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-extrabold text-amber-900">Test send</h3>
              </div>
              <input
                type="text"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="you@example.com (comma-separated for multiple)"
                className="w-full px-2 py-1 border border-amber-200 rounded text-sm mb-2 focus:border-amber-500 outline-none"
                data-testid="broadcast-test-email"
              />
              <button
                onClick={sendTest}
                disabled={sending || saving || readOnly}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold py-2 rounded inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="broadcast-test-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send test to my inbox
              </button>
              <p className="text-[11px] text-amber-800 mt-1">Saves first, then sends just this address a [TEST] copy with the first recipient's merge data filled in.</p>
            </div>

            {/* Send / Schedule */}
            {!readOnly && (
              <div className="bg-white border-2 border-emerald-300 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Send className="w-4 h-4 text-emerald-600" />
                  <h3 className="text-sm font-extrabold text-emerald-900">Send</h3>
                </div>
                <button
                  onClick={sendNow}
                  disabled={sending || saving || audience.total === 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-extrabold py-2.5 rounded inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="broadcast-send-now"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send now to {audience.total} recipient{audience.total === 1 ? '' : 's'}
                </button>
                <div className="border-t border-slate-100 pt-2">
                  <label className="text-[11px] font-bold text-slate-700 block mb-1 flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" /> Or schedule for later:
                  </label>

                  <label className="flex items-start gap-2 text-xs cursor-pointer bg-violet-50 border border-violet-200 rounded p-2 mb-2">
                    <input
                      type="checkbox"
                      checked={smartSend}
                      onChange={e => setSmartSend(e.target.checked)}
                      className="mt-0.5"
                      data-testid="broadcast-smart-toggle"
                    />
                    <div>
                      <p className="font-extrabold text-violet-900 leading-tight">
                        <Sparkles className="w-3 h-3 inline -mt-1" /> Smart Send Time
                      </p>
                      <p className="text-[11px] text-violet-800 leading-snug mt-0.5">
                        Send each recipient at <b>their</b> local time (derived from practice state).
                        Open rates typically rise 15-25%.
                      </p>
                    </div>
                  </label>

                  {smartSend ? (
                    <div className="space-y-2 mb-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 block">Date (each recipient's local)</label>
                        <input
                          type="date"
                          value={smartDate}
                          onChange={e => setSmartDate(e.target.value)}
                          className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:border-emerald-500 outline-none"
                          data-testid="broadcast-smart-date"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 block">Local hour (0–23)</label>
                        <select
                          value={smartHour}
                          onChange={e => setSmartHour(parseInt(e.target.value, 10))}
                          className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:border-emerald-500 outline-none"
                          data-testid="broadcast-smart-hour"
                        >
                          {Array.from({ length: 24 }, (_, h) => (
                            <option key={h} value={h}>
                              {h === 0 ? '12 AM (midnight)'
                                : h < 12 ? `${h} AM`
                                : h === 12 ? '12 PM (noon)'
                                : `${h - 12} PM`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="text-[10px] text-slate-500 leading-snug">
                        Example: Eastern recipients receive it at {smartHour === 0 ? '12 AM' : smartHour < 12 ? `${smartHour} AM ET` : smartHour === 12 ? '12 PM ET' : `${smartHour - 12} PM ET`}; Pacific recipients receive it 3 hours later in clock time but at the same local hour.
                      </p>

                      <label className="flex items-start gap-2 text-xs cursor-pointer bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                        <input
                          type="checkbox"
                          checked={useLearnedHours}
                          onChange={e => setUseLearnedHours(e.target.checked)}
                          className="mt-0.5"
                          data-testid="broadcast-learned-toggle"
                        />
                        <div>
                          <p className="font-extrabold text-amber-900 leading-tight flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Use each person's learned best time
                          </p>
                          <p className="text-[11px] text-amber-800 leading-snug mt-0.5">
                            For recipients with <b>≥ 3 prior opens</b>, override the {smartHour}:00 default with the hour they're most likely to open.
                            {bestHourStats && (
                              <> Right now <b>{bestHourStats.learnableRecipients}</b> of
                              your {bestHourStats.uniqueRecipientsWithOpens} tracked recipient{bestHourStats.uniqueRecipientsWithOpens === 1 ? '' : 's'}
                              {' '}qualif{bestHourStats.learnableRecipients === 1 ? 'ies' : 'y'} for an override.</>
                            )}
                          </p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <input
                      type="datetime-local"
                      value={scheduleAt}
                      onChange={e => setScheduleAt(e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm mb-2 focus:border-emerald-500 outline-none"
                      data-testid="broadcast-schedule-at"
                    />
                  )}

                  <button
                    onClick={schedule}
                    disabled={sending || saving || audience.total === 0}
                    className="w-full bg-slate-700 hover:bg-slate-800 text-white text-sm font-bold py-2 rounded inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="broadcast-schedule"
                  >
                    <CalendarClock className="w-4 h-4" />
                    {smartSend ? 'Schedule (Smart Send Time)' : 'Schedule'}
                  </button>
                </div>
              </div>
            )}

            {/* Save */}
            {!readOnly && (
              <button
                onClick={persist}
                disabled={saving}
                className="w-full bg-white hover:bg-slate-50 border-2 border-slate-300 text-slate-800 text-sm font-bold py-2 rounded inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="broadcast-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save draft
              </button>
            )}

            {/* Sent receipt */}
            {readOnly && stats && (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="font-extrabold text-emerald-900">Delivery report</span>
                </div>
                <p className="text-xs text-emerald-900">
                  <b>{stats.sent}</b> sent · <b>{stats.failed}</b> failed · <b>{stats.recipients}</b> total
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer error/toast */}
        {(err || toast) && (
          <div className="border-t border-slate-200 px-5 py-2 bg-slate-50">
            {err && (
              <div className="text-rose-800 bg-rose-50 border border-rose-200 rounded px-3 py-1.5 text-sm flex items-start gap-2" data-testid="broadcast-composer-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}
            {toast && (
              <div className={`text-sm rounded px-3 py-1.5 mt-1 ${toast.kind === 'ok' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' : 'bg-rose-50 text-rose-900 border border-rose-200'}`} data-testid="broadcast-composer-toast">
                {toast.msg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
